import { useCallback, useEffect, useRef, useState } from 'react';
import type { Timestamp } from 'firebase/firestore';
import toast from 'react-hot-toast';
import {
  backfillDialColorFromModel,
  backfillParentVariantAggregates,
  callRepropagateBrandToRefs,
  getEnrichmentRunsForBrand,
  recordEnrichmentRun,
  runDescriptionRewriteForBrand,
  type EnrichmentRunRecord,
  type EnrichmentTaskKey,
} from '../../services/firebase';
import type { WatchBrand } from '../../types';
import type { BackfillDialColorFields } from '../../types/dialColorBackfill';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { BulkOccasionExtractor } from './BulkOccasionExtractor';
import { BulkCalibreEnricher } from './BulkCalibreEnricher';
import { BulkModuleEnricher } from './BulkModuleEnricher';
import { BulkAIEnrich } from './BulkAIEnrich';
import { ReferenceCleanupPage } from '../ReferenceCleanup/ReferenceCleanupPage';

interface EnrichmentTasksPanelProps {
  brand: WatchBrand;
  /** Called after a task that may mutate ref data finishes (an inline "Run", or
   *  returning from a launch tool), so the host page can re-fetch its list. */
  onTaskCompleted?: () => void;
}

/** Launch-style tasks open their dedicated tool in a host modal (`launch:<key>`
 *  keys map to a rendered tool below). */
type LaunchKey = Extract<EnrichmentTaskKey, 'occasion' | 'calibre' | 'module' | 'aiEnrich' | 'cleanup'>;

type TaskDef =
  | {
      key: EnrichmentTaskKey;
      step: number;
      name: string;
      description: string;
      kind: 'inline';
    }
  | {
      key: LaunchKey;
      step: number;
      name: string;
      description: string;
      kind: 'launch';
    };

// James's preferred post-import order. `inline` tasks run scoped to the brand
// right here; `launch` tasks open their dedicated (interactive) tool in a modal
// over this page — so the brand list / pagination / scroll position behind it
// are preserved. They can't run headlessly without changing behaviour.
const TASKS: TaskDef[] = [
  {
    key: 'occasion',
    step: 1,
    name: 'Occasion Tagger',
    description: 'Assign occasion tags to refs from name/model heuristics (manual review).',
    kind: 'launch',
  },
  {
    key: 'calibre',
    step: 2,
    name: 'Calibre Enrichment',
    description: 'Reconcile ref calibre values against the calibre DB, then push specs back.',
    kind: 'launch',
  },
  {
    key: 'module',
    step: 3,
    name: 'Module Enrichment',
    description: "Reconcile ref electronics.module values against the module DB — link matches, or AI-create + link new modules.",
    kind: 'launch',
  },
  {
    key: 'description',
    step: 4,
    name: 'Description Rewrite',
    description: 'AI-rewrite descriptions on refs not yet rewritten or hand-edited.',
    kind: 'inline',
  },
  {
    key: 'aiEnrich',
    step: 5,
    name: 'AI Enrich (full)',
    description:
      'AI-fill every spec field across the brand, then review a per-field grid and write back only the cells you accept.',
    kind: 'launch',
  },
  {
    key: 'cleanup',
    step: 6,
    name: 'Reference Cleanup',
    description: 'Scan & merge duplicate references for this brand.',
    kind: 'launch',
  },
  {
    key: 'dialColor',
    step: 7,
    name: 'Backfill dialColor from model',
    description: 'Fill empty dial colours from colour tokens in the model/product name.',
    kind: 'inline',
  },
  {
    key: 'variantAggregates',
    step: 8,
    name: 'Re-aggregate variant fields to parent',
    description:
      "Recompute each parent's dialColour/movement/strap/case variant arrays from its variants (fixes drift after manual edits).",
    kind: 'inline',
  },
  {
    key: 'repropagateRefs',
    step: 9,
    name: 'Re-propagate brand fields to references',
    description:
      "Re-copy this brand's name, display name, country, parent organisation, microbrand and promotion " +
      "fields onto every reference (and so the search index). Normally automatic on a brand save; run it " +
      'when references show stale brand values. Safe to repeat; up to ~9 min for a large brand.',
    kind: 'inline',
  },
];

function formatRelativeTime(ts: Timestamp | null): string {
  if (!ts) return '';
  const date = ts.toDate();
  const diffMs = Date.now() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 30) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

const STORAGE_KEY = 'referenceBrowser:enrichmentTasks:collapsed';

// dialColor field-selector options (radio group). 'all' first is the default.
const DIAL_FIELD_OPTIONS: { value: BackfillDialColorFields; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'color', label: 'Color' },
  { value: 'style', label: 'Style' },
  { value: 'finish', label: 'Finish' },
];

/** Display suffix for the dialColor task name reflecting the field selection. */
function dialFieldNameSuffix(fields: BackfillDialColorFields): string {
  return fields === 'all' ? 'All' : `${DIAL_FIELD_OPTIONS.find((o) => o.value === fields)?.label} only`;
}

export function EnrichmentTasksPanel({ brand, onTaskCompleted }: EnrichmentTasksPanelProps) {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return true;
    }
  });
  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const [runs, setRuns] = useState<Partial<Record<EnrichmentTaskKey, EnrichmentRunRecord>>>({});
  const [loadingRuns, setLoadingRuns] = useState(true);
  // Which task is currently executing inline (only one at a time).
  const [runningKey, setRunningKey] = useState<EnrichmentTaskKey | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  // dialColor task: when true, recompute & overwrite EXISTING dial colors (and
  // clear ones that no longer match) instead of only filling empty ones. Default
  // off so an accidental Run never wipes correct colors.
  const [dialColorOverwrite, setDialColorOverwrite] = useState(false);
  // dialColor task: which dimension(s) to WRITE this run. 'all' (default) keeps
  // the original behavior (color + style + finish); the others restrict the write
  // to a single field. Orthogonal to the overwrite toggle above.
  const [dialColorFields, setDialColorFields] = useState<BackfillDialColorFields>('all');
  const stopRef = useRef(false);
  // Which interactive tool is open in the host modal (null = none).
  const [activeTool, setActiveTool] = useState<LaunchKey | null>(null);

  const refreshRuns = useCallback(async () => {
    try {
      const r = await getEnrichmentRunsForBrand(brand.id);
      setRuns(r);
    } catch (err) {
      console.error('Failed to load enrichment runs', err);
    } finally {
      setLoadingRuns(false);
    }
  }, [brand.id]);

  useEffect(() => {
    setLoadingRuns(true);
    void refreshRuns();
  }, [refreshRuns]);

  const runDescription = useCallback(async () => {
    stopRef.current = false;
    setRunningKey('description');
    setProgress({ done: 0, total: 0 });
    try {
      const res = await runDescriptionRewriteForBrand(brand.id, brand.name, {
        onProgress: (done, total) => setProgress({ done, total }),
        shouldStop: () => stopRef.current,
      });
      const ok = res.failed === 0 && !res.stopped;
      const summary = res.stopped
        ? `Stopped — ${res.updated} updated, ${res.failed} failed`
        : `Updated ${res.updated} of ${res.total} ref${res.total === 1 ? '' : 's'}` +
          (res.failed ? `, ${res.failed} failed` : '');
      await recordEnrichmentRun(brand.id, 'description', { ok, summary });
      (ok ? toast.success : toast)(summary);
    } catch (err) {
      console.error('Description rewrite failed', err);
      const msg = err instanceof Error ? err.message : 'Description rewrite failed';
      await recordEnrichmentRun(brand.id, 'description', { ok: false, summary: msg });
      toast.error(msg);
    } finally {
      setRunningKey(null);
      setProgress(null);
      void refreshRuns();
      onTaskCompleted?.();
    }
  }, [brand.id, brand.name, refreshRuns, onTaskCompleted]);

  const runDialColor = useCallback(async () => {
    setRunningKey('dialColor');
    try {
      const r = await backfillDialColorFromModel({
        dryRun: false,
        brandId: brand.id,
        overwriteExisting: dialColorOverwrite,
        fields: dialColorFields,
      });
      const matched = r.singleValueMatched + r.multiValueMatched;
      const cleared = r.colorCleared ?? 0;
      const summary =
        `Updated ${r.updated} ref${r.updated === 1 ? '' : 's'} ` +
        `(${matched} matched of ${r.candidatesWithEmptyColor} empty)` +
        (dialColorOverwrite ? ` [re-derive]` : '') +
        (cleared ? `, ${cleared} cleared` : '') +
        (r.ambiguous.length ? `, ${r.ambiguous.length} ambiguous` : '') +
        (r.errors.length ? `, ${r.errors.length} error(s)` : '');
      const ok = r.errors.length === 0;
      await recordEnrichmentRun(brand.id, 'dialColor', { ok, summary });
      (ok && !r.ambiguous.length ? toast.success : toast)(summary, { icon: ok ? undefined : '⚠️' });
    } catch (err) {
      console.error('Backfill dialColor failed', err);
      const msg = err instanceof Error ? err.message : 'Backfill dialColor failed';
      await recordEnrichmentRun(brand.id, 'dialColor', { ok: false, summary: msg });
      toast.error(msg);
    } finally {
      setRunningKey(null);
      void refreshRuns();
      onTaskCompleted?.();
    }
  }, [brand.id, refreshRuns, onTaskCompleted, dialColorOverwrite, dialColorFields]);

  const runVariantAggregates = useCallback(async () => {
    setRunningKey('variantAggregates');
    try {
      const r = await backfillParentVariantAggregates({ brandId: brand.id });
      const summary =
        `Updated ${r.parentsUpdated} parent${r.parentsUpdated === 1 ? '' : 's'} ` +
        `of ${r.scanned} with variants` +
        (r.errors.length ? `, ${r.errors.length} error(s)` : '');
      const ok = r.errors.length === 0;
      await recordEnrichmentRun(brand.id, 'variantAggregates', { ok, summary });
      (ok ? toast.success : toast)(summary, { icon: ok ? undefined : '⚠️' });
    } catch (err) {
      console.error('Re-aggregate variant fields failed', err);
      const msg = err instanceof Error ? err.message : 'Re-aggregate variant fields failed';
      await recordEnrichmentRun(brand.id, 'variantAggregates', { ok: false, summary: msg });
      toast.error(msg);
    } finally {
      setRunningKey(null);
      void refreshRuns();
      onTaskCompleted?.();
    }
  }, [brand.id, refreshRuns, onTaskCompleted]);

  // Moved here from the brand edit form: it maintains the brand's references,
  // not the brand record. Same flag as before decides whether it is offered.
  const showRepropagate = useFeatureFlag('tools.showRepropagateBrandToRefs');
  const tasks = showRepropagate ? TASKS : TASKS.filter((t) => t.key !== 'repropagateRefs');

  const runRepropagate = useCallback(async () => {
    const confirmed = window.confirm(
      `Re-propagate "${brand.name}" to all of its reference records? This can take up to 9 minutes for large brands.`,
    );
    if (!confirmed) return;
    setRunningKey('repropagateRefs');
    try {
      const { touched, scanned } = await callRepropagateBrandToRefs(brand.id);
      const summary =
        `Re-propagated to ${touched} reference${touched === 1 ? '' : 's'}` +
        (typeof scanned === 'number' ? ` (of ${scanned} scanned)` : '');
      await recordEnrichmentRun(brand.id, 'repropagateRefs', { ok: true, summary });
      toast.success(summary);
    } catch (err) {
      console.error('repropagateBrandToRefs failed', err);
      const msg = err instanceof Error ? `Re-propagate failed: ${err.message}` : 'Re-propagate failed';
      await recordEnrichmentRun(brand.id, 'repropagateRefs', { ok: false, summary: msg });
      toast.error(msg);
    } finally {
      setRunningKey(null);
      void refreshRuns();
      onTaskCompleted?.();
    }
  }, [brand.id, brand.name, refreshRuns, onTaskCompleted]);

  const handleRunInline = (key: EnrichmentTaskKey) => {
    if (key === 'description') void runDescription();
    else if (key === 'dialColor') void runDialColor();
    else if (key === 'variantAggregates') void runVariantAggregates();
    else if (key === 'repropagateRefs') void runRepropagate();
  };

  const closeTool = useCallback(async () => {
    const justClosed = activeTool;
    setActiveTool(null);
    // Returning from a launch tool (close / Escape / backdrop) counts as
    // completing the step — opening + returning is enough, no run required.
    // These interactive tools don't write their own run record, so this is the
    // only stamp they get. Mirrors the "Run" tasks' auto-record on completion.
    if (justClosed) {
      try {
        await recordEnrichmentRun(brand.id, justClosed, { ok: true, summary: 'Marked done' });
      } catch (err) {
        console.error('Failed to auto-mark task done', err);
      }
    }
    // Pick up the new last-run stamp + any state the tool surfaced.
    void refreshRuns();
    // The interactive tool may have mutated ref data — refresh the host list too.
    onTaskCompleted?.();
  }, [activeTool, brand.id, refreshRuns, onTaskCompleted]);

  // Bridge to BulkAIEnrich's unapplied-results guard. Only that tool registers
  // one — for the other four, `guardedCloseTool` is just `closeTool`.
  const enrichExitGuardRef = useRef<((proceed: () => void) => void) | null>(null);
  const guardedCloseTool = useCallback(() => {
    const guard = enrichExitGuardRef.current;
    if (guard) guard(() => void closeTool());
    else void closeTool();
  }, [closeTool]);

  const busy = runningKey !== null;

  // Title shown on the host modal for the open interactive tool.
  const toolTitle = activeTool ? TASKS.find((t) => t.key === activeTool)?.name ?? '' : '';

  return (
    <>
    <section className="rounded-lg border border-gray-200 bg-white">
      <button
        type="button"
        onClick={toggleCollapsed}
        aria-expanded={!collapsed}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left cursor-pointer group"
      >
        <span className="flex items-center gap-2">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 group-hover:text-gray-700">
            Enrichment Tasks
          </h3>
          <span className="text-xs text-gray-400">post-import · run in order</span>
        </span>
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform ${collapsed ? '-rotate-90' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {!collapsed && (
        <ul className="divide-y divide-gray-100 border-t border-gray-100">
          {tasks.map((task) => {
            const run = runs[task.key];
            const isThisRunning = runningKey === task.key;
            return (
              <li
                key={task.key}
                className="flex items-start gap-3 px-4 py-3 flex-wrap sm:flex-nowrap"
              >
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-600">
                  {task.step}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">
                      {task.key === 'dialColor'
                        ? `Backfill: ${dialFieldNameSuffix(dialColorFields)}`
                        : task.name}
                    </span>
                    {task.kind === 'launch' && (
                      <span className="rounded-full bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700">
                        opens tool
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">{task.description}</p>
                  {task.key === 'dialColor' && (
                    <fieldset className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600">
                      <span className="font-medium text-gray-500">Fields:</span>
                      {DIAL_FIELD_OPTIONS.map((opt) => (
                        <label key={opt.value} className="flex items-center gap-1">
                          <input
                            type="radio"
                            name="dialColorFields"
                            className="h-3.5 w-3.5 border-gray-300"
                            value={opt.value}
                            checked={dialColorFields === opt.value}
                            disabled={busy}
                            onChange={() => setDialColorFields(opt.value)}
                          />
                          <span>{opt.label}</span>
                        </label>
                      ))}
                      <span
                        className="cursor-help text-gray-400"
                        title="Restrict which dial field(s) this run WRITES. Color → dialAndHands.color only · Style → dialStyle only · Finish → dialAndHands.finish only · All (default) → all three. Detection always derives all three; this only gates the writes. Orthogonal to Re-derive."
                      >
                        ⓘ
                      </span>
                    </fieldset>
                  )}
                  {task.key === 'dialColor' && (
                    <label className="mt-1 flex items-center gap-1.5 text-xs text-gray-600">
                      <input
                        type="checkbox"
                        className="h-3.5 w-3.5 rounded border-gray-300"
                        checked={dialColorOverwrite}
                        disabled={busy}
                        onChange={(e) => setDialColorOverwrite(e.target.checked)}
                      />
                      <span>Re-derive (overwrite existing colours)</span>
                      <span
                        className="cursor-help text-gray-400"
                        title="Recompute EVERY ref's dial colour from the model/product name, not just empty ones — and clear colours that no longer match. Use this after correcting noise patterns / anchor words. Off by default so a normal Run never wipes correct colours."
                      >
                        ⓘ
                      </span>
                    </label>
                  )}
                  <p className="mt-1 text-xs">
                    {loadingRuns ? (
                      <span className="text-gray-300">Loading…</span>
                    ) : run ? (
                      <span className={run.ok ? 'text-gray-500' : 'text-amber-700'}>
                        {run.ok ? '✓' : '⚠'} {run.summary}
                        {run.ranAt && (
                          <span className="text-gray-400"> · {formatRelativeTime(run.ranAt)}</span>
                        )}
                        {run.ranBy && <span className="text-gray-400"> · {run.ranBy}</span>}
                      </span>
                    ) : (
                      <span className="text-gray-400 italic">Never run</span>
                    )}
                  </p>
                  {isThisRunning && task.key === 'description' && progress && progress.total > 0 && (
                    <div className="mt-2 max-w-xs">
                      <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
                        <div
                          className="h-1.5 rounded-full bg-indigo-600 transition-all"
                          style={{ width: `${(progress.done / progress.total) * 100}%` }}
                        />
                      </div>
                      <p className="mt-1 text-[11px] text-gray-500">
                        {progress.done} / {progress.total} processed
                      </p>
                    </div>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {task.kind === 'inline' ? (
                    isThisRunning && task.key === 'description' ? (
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => {
                          stopRef.current = true;
                        }}
                      >
                        Stop
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => handleRunInline(task.key)}
                        loading={isThisRunning}
                        disabled={busy}
                      >
                        Run
                      </Button>
                    )
                  ) : (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setActiveTool(task.key as LaunchKey)}
                      disabled={busy}
                      title="Opens the tool; returning marks this step done"
                    >
                      Open tool →
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>

    {/* Host modal — renders the interactive tool inline over this page so the
        brand list / pagination / scroll position behind it stay intact. Each
        tool is mounted only while open (fresh mount seeds the locked brand) and
        unmounted on close. `embedded` hides the tool's own page header + brand
        picker. */}
    <Modal
      isOpen={activeTool !== null}
      onClose={guardedCloseTool}
      // Only the AI-enrich tool holds results a stray backdrop click would
      // destroy; the other four are re-openable at no cost, so they keep the
      // usual click-outside-to-dismiss.
      dismissOnBackdrop={activeTool !== 'aiEnrich'}
      title={`${toolTitle} — ${brand.name}`}
      size="full"
    >
      {activeTool === 'occasion' && (
        <BulkOccasionExtractor embedded initialBrandId={brand.id} />
      )}
      {activeTool === 'calibre' && (
        <BulkCalibreEnricher embedded initialBrandId={brand.id} />
      )}
      {activeTool === 'module' && (
        <BulkModuleEnricher embedded initialBrandId={brand.id} />
      )}
      {activeTool === 'aiEnrich' && (
        <BulkAIEnrich
          embedded
          initialBrandId={brand.id}
          onApplied={onTaskCompleted}
          onApplySucceeded={() => void closeTool()}
          exitGuardRef={enrichExitGuardRef}
        />
      )}
      {activeTool === 'cleanup' && (
        <ReferenceCleanupPage embedded initialBrandId={brand.id} />
      )}
    </Modal>
    </>
  );
}
